
import { useEffect } from "react";
import { useNavigate, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/components/AuthProvider";
import { StudentDashboardHeader } from "@/components/student-dashboard/StudentDashboardHeader";
import { StudentDashboardSidebar } from "@/components/student-dashboard/StudentDashboardSidebar";
import { MobileMenuProvider } from "@/contexts/MobileMenuContext";
import { StudentWorkoutsList } from "@/components/student-dashboard/workouts/StudentWorkoutsList";
import { StudentTrainingSessionsList } from "@/components/student-dashboard/training-sessions/StudentTrainingSessionsList";
import { StudentNutritionList } from "@/components/student-dashboard/nutrition/StudentNutritionList";
import { StudentAssessmentsList } from "@/components/student-dashboard/assessments/StudentAssessmentsList";
import { StudentProgress } from "@/components/student-dashboard/progress/StudentProgress";
import { StudentSettings } from "@/components/student-dashboard/settings/StudentSettings";
import { StudentSchedule } from "@/components/student-dashboard/schedule/StudentSchedule";
import NewStudentTrainingSession from "./student-dashboard/NewStudentTrainingSession";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MobileNavBar } from "@/components/shared/MobileNavBar";
import { StudentDashboardOverview } from "@/components/student-dashboard/StudentDashboardOverview";
import { ThemeProvider } from "@/components/ThemeProvider";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath, getLanguagePath } from "@/utils/language";
import { MessagesPage } from "@/components/messaging/MessagesPage";

type StudentProfile = {
  id: string;
  has_schedule_access?: boolean;
  [key: string]: any;
}

const StudentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const { getPath } = useLanguagePath();
  
  const { data: studentProfile } = useQuery({
    queryKey: ["student-profile", user?.id, user?.email],
    queryFn: async () => {
      if (!user?.id) return null;
      
      console.log('Fetching student profile by auth_id:', user.id);
      
      // Melhorada: Primeiro tentar por auth_id, depois por email para compatibilidade
      let data = null;
      let error = null;

      // Tentar primeiro por auth_id (estudantes com conta registada)
      const { data: authIdResult, error: authIdError } = await supabase
        .from("students")
        .select("*")
        .eq("auth_id", user.id)
        .eq("status", "active")
        .maybeSingle();
        
      if (authIdResult) {
        data = authIdResult;
        console.log('Student profile found by auth_id:', data);
      } else if (user.email) {
        // Se não encontrou por auth_id, tentar por email (estudantes manuais)
        console.log('Student profile not found by auth_id, trying email:', user.email);
        const { data: emailResult, error: emailError } = await supabase
          .from("students")
          .select("*")
          .eq("email", user.email)
          .eq("status", "active")
          .maybeSingle();
          
        data = emailResult;
        error = emailError;
        
        if (data) {
          console.log('Student profile found by email:', data);
        }
      } else {
        error = authIdError;
      }
        
      if (error) {
        console.error("Error fetching student profile:", error);
        return null;
      }
      
      return data as StudentProfile;
    },
    enabled: !!user?.id
  });

  const hasScheduleAccess = studentProfile?.has_schedule_access !== false;

  useEffect(() => {
    if (!user) {
      navigate(getPath("/student-auth"));
      return;
    }
    
    // Verificar se estamos na URL correta do idioma
    const currentLangPrefix = location.pathname.split('/')[1];
    const needsRedirect = (
      (language === 'pt' && currentLangPrefix !== 'pt') ||
      (language === 'es' && currentLangPrefix !== 'es') ||
      (language === 'fr' && currentLangPrefix !== 'fr') ||
      (language === 'en' && ['pt', 'es', 'fr'].includes(currentLangPrefix))
    );

    if (needsRedirect) {
      // Extrair o caminho sem o prefixo de idioma
      let pathWithoutPrefix = location.pathname;
      if (['pt', 'es', 'fr'].includes(currentLangPrefix)) {
        pathWithoutPrefix = '/' + location.pathname.split('/').slice(2).join('/');
      }
      
      // Navegue para o caminho correto com o prefixo de idioma apropriado
      navigate(getLanguagePath(pathWithoutPrefix, language), { replace: true });
    }
  }, [user, navigate, getPath, language, location.pathname]);

  if (!user) return null;

  return (
    <ThemeProvider>
      <MobileMenuProvider>
        <div className="min-h-screen bg-gradient-to-br from-[#F9F9F9] via-[#F5F7FA] to-[#FCFCFC] dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-850 dark:to-gray-800 overflow-x-hidden pb-20 md:pb-0 relative">
          <div 
            className="absolute inset-0 w-full h-full z-0 opacity-20 dark:opacity-10" 
            style={{
              backgroundImage: 'radial-gradient(#0000001a 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }}
          />
          
          <StudentDashboardHeader />
          <div className="flex overflow-x-hidden">
            <StudentDashboardSidebar />
            <main className="flex-1 p-4 md:p-6 ml-0 md:ml-[18rem] mt-20 relative z-0 w-full max-w-full overflow-x-hidden">
              {!studentProfile ? (
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-xl shadow-lg p-8 text-center border border-slate-100 dark:border-gray-700">
                  <h2 className="text-2xl font-bold mb-2 dark:text-white">Conta não associada</h2>
                  <p className="text-muted-foreground dark:text-gray-300">
                    A sua conta ainda não está associada a nenhum personal trainer.
                    Por favor, contacte o seu personal trainer para que ele associe a sua conta ao sistema.
                  </p>
                </div>
              ) : (
                <div className="relative z-10 w-full">
                  <Routes>
                    <Route path="/" element={<StudentDashboardOverview />} />
                    <Route path="/schedule" element={
                      hasScheduleAccess ? (
                        <StudentSchedule />
                      ) : (
                        <Navigate to={getPath("/student-dashboard")} replace />
                      )
                    } />
                    <Route path="/workouts" element={<StudentWorkoutsList />} />
                    <Route path="/training-sessions" element={<StudentTrainingSessionsList />} />
                    <Route path="/training-sessions/new" element={<NewStudentTrainingSession />} />
                    <Route path="/nutrition" element={<StudentNutritionList />} />
                    <Route path="/assessments" element={<StudentAssessmentsList />} />
                    <Route path="/progress" element={<StudentProgress />} />
                    <Route path="/settings" element={<StudentSettings />} />
                    <Route path="/messages" element={<MessagesPage />} />
                  </Routes>
                </div>
              )}
            </main>
          </div>
          <MobileNavBar dashboardType="student" />
        </div>
      </MobileMenuProvider>
    </ThemeProvider>
  );
};

export default StudentDashboard;
